// redux/slices/easycardConnectSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

const getError = (error, fallback) =>
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback;

const handleAuthError = async (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
    }
};

const getUid = (getState, providedUid) => {
    const state = getState();
    const uid =
        providedUid ||
        state?.master?.master?.uid ||
        state?.master?.uid ||
        state?.auth?.uid ||
        null;
    if (!uid) throw new Error("UID required");
    return uid;
};

export const saveEasycardCreds = createAsyncThunk(
    "easycardConnect/save",
    async ({ uid: uidArg, terminalId, apiKey, defaults } = {}, { getState, rejectWithValue }) => {
        try {
            const uid = getUid(getState, uidArg);
            const res = await apiRequest("POST", "/payments/easycard/credentials", {
                uid, terminalId, apiKey, defaults,
            });
            return res?.data || res;
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(getError(error, "Save EasyCard credentials failed"));
        }
    }
);

export const fetchEasycardStatus = createAsyncThunk(
    "easycardConnect/status",
    async (uidArg, { getState, rejectWithValue }) => {
        try {
            const uid = getUid(getState, uidArg);
            const res = await apiRequest("GET", `/payments/easycard/credentials/${encodeURIComponent(uid)}`);
            return res?.data || res;
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(getError(error, "Fetch EasyCard status failed"));
        }
    }
);

export const disconnectEasycard = createAsyncThunk(
    "easycardConnect/disconnect",
    async (uidArg, { getState, rejectWithValue }) => {
        try {
            const uid = getUid(getState, uidArg);
            const res = await apiRequest("DELETE", `/payments/easycard/credentials/${encodeURIComponent(uid)}`);
            return res?.data || res || { ok: true };
        } catch (error) {
            await handleAuthError(error);
            return rejectWithValue(getError(error, "Disconnect EasyCard failed"));
        }
    }
);

const applyStatus = (state, payload = {}) => {
    state.connected = !!payload?.connected;
    state.keyMask = payload?.keyMask || null;
    state.terminalId = payload?.terminalId || null;
    state.updatedAt = payload?.updatedAt || null;
    state.defaults = payload?.defaults || null;
};

const initialState = {
    connected: false,
    keyMask: null,
    terminalId: null,
    updatedAt: null,
    defaults: null,
    status: "idle",
    saving: false,
    error: null,
};

const easycardConnectSlice = createSlice({
    name: "easycardConnect",
    initialState,
    reducers: {
        clearEasycardError: (state) => { state.error = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(saveEasycardCreds.pending, (state) => { state.saving = true; state.error = null; })
            .addCase(saveEasycardCreds.fulfilled, (state, action) => {
                state.saving = false;
                if (action.payload?.connected !== undefined) applyStatus(state, action.payload);
            })
            .addCase(saveEasycardCreds.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload || action.error?.message || "Save failed";
            })

            .addCase(fetchEasycardStatus.pending, (state) => { state.status = "loading"; state.error = null; })
            .addCase(fetchEasycardStatus.fulfilled, (state, action) => {
                state.status = "succeeded";
                applyStatus(state, action.payload);
            })
            .addCase(fetchEasycardStatus.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload || action.error?.message || "Status failed";
            })

            .addCase(disconnectEasycard.pending, (state) => { state.saving = true; state.error = null; })
            .addCase(disconnectEasycard.fulfilled, (state) => {
                state.saving = false;
                applyStatus(state, {});
            })
            .addCase(disconnectEasycard.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload || action.error?.message || "Disconnect failed";
            })

            .addCase(resetStore, () => initialState);
    },
});

export const { clearEasycardError } = easycardConnectSlice.actions;

export const selectEasycardConnect = (state) => state.easycardConnect ?? initialState;
export const selectEasycardConnected = (state) => !!selectEasycardConnect(state).connected;

export default easycardConnectSlice.reducer;